import { ListItemButton, TextField } from "@mui/material";
import type { RenderFilterProps } from "@shared/frontend/hyper-table/filter-types";
import { useHyperTable } from "@shared/frontend/hyper-table/HyperTable";
import { generateHyperFilterFromPath } from "@shared/helpers/sql-relations";
import React, { useCallback } from "react";
import CustomFilter from "./CustomFilter";
import type { EntityFilterProps } from "./helpers";
import { getProcess, SELECT_OPERATORS } from "./helpers";

type NumberFilterProps<T> = EntityFilterProps<T>

function asFilterNumber(value: unknown): number {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : 0;
}

const NumberFilter = <T extends any>(props: NumberFilterProps<T>) => {
  const { filter, closeMenu } = props;
  const { getEntity } = useHyperTable<T>();
  const handleSave = useCallback(
    (
      onSave: RenderFilterProps<T>["onSave"],
      value: number,
      selectValue: string | undefined,
    ) => {
      const number = asFilterNumber(value);
      const entity = getEntity();
      const isRelationalField =
        "hyperFilter" in entity && filter.fieldName.includes(".");

      onSave(
        (prev) => {
          return {
            value: isRelationalField
              ? (entity as any).hyperFilter(
                  generateHyperFilterFromPath(
                    entity,
                    filter.fieldName,
                    getProcess("number", selectValue)(number),
                  ),
                )
              : {
                  ...prev.value,
                  [filter.fieldName]: getProcess("number", selectValue)(number),
                },
            meta: {
              ...prev.meta,
              selectOperatorValue: selectValue,
              number,
            },
          };
        },
        { closeOnSave: false },
      );
    },
    [filter.fieldName, getEntity()],
  );

  return (
    <CustomFilter<T>
      closeMenu={closeMenu}
      filter={{
        icon: "number",
        ...filter,
        select: {
          initialValue: SELECT_OPERATORS.number[0].value,
          options: SELECT_OPERATORS.number.map((v) => ({
            value: v.value,
            caption: v.caption,
          })),
          onChange: ({ meta, onSave, selectValue }) => {
            handleSave(onSave, asFilterNumber(meta.number), selectValue);
          },
        },
        renderFilter: useCallback(
          ({ meta, onSave, selectValue }) => (
            <ListItemButton
              autoFocus
              sx={{
                ["&.Mui-focusVisible"]: { backgroundColor: "transparent" },
              }}
              disableRipple
              disableTouchRipple
            >
              <TextField
                name={filter.fieldName as string}
                color="secondary"
                type="number"
                value={asFilterNumber(meta.number)}
                onChange={(e) =>
                  handleSave(onSave, asFilterNumber(e.target.value), selectValue)
                }
                autoFocus
              />
            </ListItemButton>
          ),
          [filter.fieldName, handleSave],
        ),
      }}
    />
  );
};

export default NumberFilter;
